import { useState } from 'react'
import { Box, Button, Snackbar, Alert } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { TITLE_CREATE_FORM } from 'routes/title-constants'
import { Title } from '../../components/common/Title'
import { RouteProps } from 'routes/AppRouter'
import { COLOR_PRIMARY } from '../../routes/color-constants'
import FormBuilder from '../../features/form-builder/FormBuilder'
import { FormsTable } from '../../features/form-builder/FormsTable'
import { FormService } from '../../modules/Form/services/FormService'
import { FORM_SERVICE_KEY } from '../../modules/Form'
import { getFormContainer } from '../../container/form-module'
import { formSchemaToDTO } from './formSchemaToDTO'
import { FormSchema, FormRowSchema, FormFieldSchema, FieldType } from '../../common/types/FormSchema'
import { NewProduct, NewProductDTO } from '../../modules/Form/models/Form'
import { v4 as uuidv4 } from 'uuid'

// ─── Conversor NewProduct → FormSchema ────────────────────────────────────────

function mapElementTypeToFieldType(type: string): FieldType {
  const map: Record<string, FieldType> = {
    text: 'text',
    number: 'number',
    email: 'email',
    textarea: 'textarea',
    select: 'select',
    checkboxGroup: 'radio',
    checkbox: 'checkbox',
    date: 'date',
  }
  return (map[type] as FieldType) ?? 'text'
}

function dtoToFormSchema(product: NewProduct): FormSchema {
  const mapRows = (rows: any[]): FormRowSchema[] =>
    (rows ?? []).map((row: any) => ({
      id: row.id ?? uuidv4(),
      items: (row.items ?? []).map((item: any) => {
        const f = item.field ?? {}
        const field: FormFieldSchema = {
          id: uuidv4(),
          type: mapElementTypeToFieldType(f.type),
          label: f.title ?? '',
          name: f.name ?? '',
          required: f.isRequired ?? false,
          options: f.options?.map((o: any) => ({ label: o.text ?? '', value: o.value ?? '' })),
        }
        return { field, width: item.width ?? 6 }
      }),
    }))

  return {
    id: product.id,
    title: product.productName,
    sections: [
      {
        id: 'section-patient',
        title: 'Datos del paciente',
        rows: mapRows(product.patientForm as any),
      },
      {
        id: 'section-sample',
        title: 'Datos de la muestra',
        rows: mapRows(product.sampleForm as any),
      },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

// ─── Vistas de la página ──────────────────────────────────────────────────────

type View = 'table' | 'create' | 'edit'

// ─────────────────────────────────────────────────────────────────────────────

export const Form = (props: RouteProps) => {
  const [view, setView] = useState<View>('table')
  const [saving, setSaving] = useState(false)

  // Formulario en edición
  const [editingForm, setEditingForm] = useState<NewProduct | null>(null)
  const [editingSchema, setEditingSchema] = useState<FormSchema | undefined>(undefined)

  // Contador para forzar refresco de la tabla tras guardar/actualizar
  const [refreshKey, setRefreshKey] = useState(0)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const formService = getFormContainer().get<FormService>(FORM_SERVICE_KEY)

  // ── Navegar a la tabla ────────────────────────────────────────────────────
  const goToTable = () => {
    setEditingForm(null)
    setEditingSchema(undefined)
    setView('table')
  }

  // ── Desde la tabla: clic en "Editar" ────────────────────────────────────
  const handleEditFromTable = (form: NewProduct) => {
    setEditingForm(form)
    setEditingSchema(dtoToFormSchema(form))
    setView('edit')
  }

  // ── Guardar nuevo formulario ──────────────────────────────────────────────
  const handleCreate = async (data: { id: string; schema: FormSchema; odooNumber: number; productName: string }) => {
    setSaving(true)
    try {
      const { patientForm, sampleForm } = formSchemaToDTO(data.schema)
      const res = await formService.add({
        id: data.id,
        productName: data.productName,
        odooNumber: data.odooNumber,
        patientForm,
        sampleForm,
      }).toPromise()
      if (res === undefined) throw new Error()
      setSnackbar({ open: true, message: 'Formulario guardado correctamente', severity: 'success' })
      setRefreshKey(k => k + 1)
      goToTable()
    } catch {
      setSnackbar({ open: true, message: 'Error al guardar el formulario', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // ── Actualizar formulario existente ──────────────────────────────────────
  const handleUpdate = async (data: { id: string; schema: FormSchema; odooNumber: number; productName: string }) => {
    setSaving(true)
    try {
      const { patientForm, sampleForm } = formSchemaToDTO(data.schema)
      const dto: NewProductDTO = {
        id: data.id,
        productName: data.productName,
        odooNumber: data.odooNumber,
        patientForm,
        sampleForm,
      }
      const res = await formService.update(data.id, dto).toPromise()
      if (res === undefined) throw new Error()
      setSnackbar({ open: true, message: `Formulario "${data.productName}" actualizado correctamente`, severity: 'success' })
      setRefreshKey(k => k + 1)
      goToTable()
    } catch {
      setSnackbar({ open: true, message: 'Error al actualizar el formulario', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }))

  // ── Títulos por vista ─────────────────────────────────────────────────────
  const titleByView: Record<View, string> = {
    table: TITLE_CREATE_FORM,
    create: 'Nuevo formulario',
    edit: `Editar: ${editingForm?.productName ?? ''}`,
  }

  return (
    <>
      <Title title={titleByView[view]} color={COLOR_PRIMARY} />

      <Box sx={{ paddingBottom: 4 }}>

        {/* ── Vista: tabla de formularios ── */}
        {view === 'table' && (
          <FormsTable
            refreshKey={refreshKey}
            onEdit={handleEditFromTable}
            onCreateNew={() => setView('create')}
          />
        )}

        {/* ── Vista: crear formulario ── */}
        {view === 'create' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={goToTable}
                sx={{ color: '#757575', textTransform: 'none' }}
              >
                Volver a la lista
              </Button>
            </Box>
            <FormBuilder
              key="new"
              onSave={handleCreate}
              isLoading={saving}
              readOnlyOdoo={false}
            />
          </>
        )}

        {/* ── Vista: editar formulario ── */}
        {view === 'edit' && editingForm && (
          <>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={goToTable}
                sx={{ color: '#757575', textTransform: 'none' }}
              >
                Volver a la lista
              </Button>
            </Box>
            <FormBuilder
              key={editingForm.id}
              initialSchema={editingSchema}
              initialOdooNumber={editingForm.odooNumber}
              initialProductName={editingForm.productName}
              onSave={handleUpdate}
              isLoading={saving}
              readOnlyOdoo={true}
            />
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
